/* ============================================================
   ARTEVERK — theme.js · BEHAVIOR ISLANDS (R2.1 foundation)
   ------------------------------------------------------------
   Small, dependency-free, deferred. Three independent islands,
   each guards its own elements so a page missing one still works:

     1. nav scroll-state   — sticky header class swap (scrolled / lit)
     2. mobile menu         — burger toggle + body scroll lock + focus trap
     3. scroll-reveal       — IntersectionObserver fade-in (perf-safe)
     4. favorites           — localStorage wishlist + heart toggles + nav count
     5–7. PDP gallery / variant / collection filters & load-more
     8. cart quantity steppers — keyed updates[] + submit

   Ported from the Direction C / collection prototypes, generalised
   to drive off DATA ATTRIBUTES (data-fav-id) instead of hard-coded
   ids so it works for any product card the Liquid renders.
   ============================================================ */
(function () {
  'use strict';

  /* ---------------------------------------------------------
     0 · GA4 EVENT HELPER
     One safe wrapper for sending custom GA4 events from the
     storefront. The Google tag stub is installed in <head> by
     the Google & YouTube app, so window.gtag exists (and queues
     to dataLayer) by the time any interaction fires.
     --------------------------------------------------------- */
  /* Conversion-grade events only — see snippets/ga4-track.liquid for why we publish a
     namespaced CUSTOM event to the Web Pixels API instead of calling fbq() here. */
  var AV_CONVERSION_EVENTS = ['generate_lead', 'newsletter_signup', 'request_appointment'];
  window.avPublishConversion = function (name, params) {
    try {
      if (AV_CONVERSION_EVENTS.indexOf(name) === -1) return;
      if (!(window.Shopify && window.Shopify.analytics &&
            typeof window.Shopify.analytics.publish === 'function')) return;
      var q = params || {};
      window.Shopify.analytics.publish('arteverk:conversion', {
        event: name, method: q.method || '', value: q.value || 0, currency: q.currency || 'USD'
      });
    } catch (e) {}
  };

  var avTrack = function (name, params) {
    try {
      if (typeof window.gtag === 'function') { window.gtag('event', name, params || {}); }
      else { (window.dataLayer = window.dataLayer || []).push({ event: name, params: params || {} }); }
    } catch (e) {}
    window.avPublishConversion(name, params);
  };

  /* High-intent contact clicks → phone_click / email_click.
     Delegated once on the document so it covers every tel:/mailto:
     link, now and any added later (NAP block, header, footer).
     Share-by-email links (which carry ?subject=) are not contact
     intent, so they're skipped. */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="tel:"], a[href^="mailto:"]') : null;
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (href.indexOf('tel:') === 0) { avTrack('phone_click', { method: 'tel' }); }
    else if (href.indexOf('mailto:') === 0 && href.indexOf('?subject=') === -1) { avTrack('email_click', { method: 'mailto' }); }
  }, true);

  /* ---------------------------------------------------------
     1 · NAV SCROLL-STATE
     - .scrolled  : shrink the island once the user scrolls.
     - .lit       : light pill / dark logo once we pass the dark
                    hero stage. A page with no dark hero marks the
                    header [data-nav-lit] so it starts lit.
     --------------------------------------------------------- */
  var header = document.getElementById('site-header');
  if (header) {
    var startsLit = header.hasAttribute('data-nav-lit');
    var hero = document.querySelector('[data-hero]');

    var onScroll = function () {
      var sc = window.scrollY > 40;
      header.classList.toggle('scrolled', sc);
      // Header is transparent ("invisible") at the top; once it gets a parchment
      // background on scroll (or on a content page) the text/logo go dark.
      header.classList.toggle('lit', startsLit || sc);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------
     2 · MOBILE MENU
     --------------------------------------------------------- */
  var burger = document.getElementById('nav-burger');
  var menu = document.getElementById('nav-overlay');
  if (burger && menu) {
    var menuFocusables = function () {
      return Array.prototype.slice.call(
        menu.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    };

    var setMenu = function (open) {
      // Mutual exclusion with the search overlay (both own the body scroll-lock).
      if (open && msOverlay && msOverlay.classList.contains('open') && typeof setSearch === 'function') setSearch(false);
      burger.classList.toggle('active', open);
      menu.classList.toggle('open', open);
      burger.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        // Move focus to the first focusable element inside the overlay.
        var f = menuFocusables();
        if (f.length) f[0].focus();
      } else {
        // Restore focus to the toggle that opened the menu.
        burger.focus();
      }
    };
    burger.addEventListener('click', function () {
      setMenu(!menu.classList.contains('open'));
    });
    menu.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', function () { setMenu(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (!menu.classList.contains('open')) return;
      if (e.key === 'Escape') { setMenu(false); return; }
      // Trap Tab within the overlay while open.
      if (e.key === 'Tab') {
        var f = menuFocusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    });
  }

  /* ---------------------------------------------------------
     2b · MOBILE SEARCH OVERLAY (mirrors the mobile menu)
     --------------------------------------------------------- */
  var msTrigger = document.getElementById('nav-search-toggle');
  var msOverlay = document.getElementById('nav-search-overlay');
  var msClose = document.getElementById('nav-search-close');
  if (msTrigger && msOverlay) {
    var msInput = msOverlay.querySelector('input[type="search"]');
    var searchFocusables = function () {
      return Array.prototype.slice.call(
        msOverlay.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    };
    var setSearch = function (open) {
      // Mutual exclusion: the menu and the search overlay both own the body
      // scroll-lock, so never let both be open at once.
      if (open && menu && menu.classList.contains('open') && typeof setMenu === 'function') setMenu(false);
      msOverlay.classList.toggle('open', open);
      msTrigger.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) { if (msInput) setTimeout(function () { msInput.focus(); }, 60); }
      else { msTrigger.focus(); }
    };
    msTrigger.addEventListener('click', function () { setSearch(!msOverlay.classList.contains('open')); });
    if (msClose) msClose.addEventListener('click', function () { setSearch(false); });
    document.addEventListener('keydown', function (e) {
      if (!msOverlay.classList.contains('open')) return;
      if (e.key === 'Escape') { setSearch(false); return; }
      // Trap Tab inside the dialog while open (honors aria-modal="true").
      if (e.key === 'Tab') {
        var f = searchFocusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---------------------------------------------------------
     3 · SCROLL-REVEAL (IntersectionObserver)
     Honors prefers-reduced-motion by simply revealing everything.
     --------------------------------------------------------- */
  var reveals = document.querySelectorAll('.reveal');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reveals.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('in'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
      reveals.forEach(function (el) {
        // A reveal block taller than the viewport can never reach a positive
        // intersection threshold, so it would stay hidden forever (this hid the
        // long blog/guide bodies on phones). Reveal those immediately — content
        // must never depend on the scroll animation firing. Normal-height
        // elements still animate in on scroll.
        if (el.offsetHeight >= window.innerHeight) { el.classList.add('in'); }
        else { io.observe(el); }
      });
    }
  }

  /* ---------------------------------------------------------
     4 · FAVORITES (wishlist)
     State lives in localStorage under 'arteverk_favs' as an array
     of product ids. Every [data-fav-id] button toggles membership;
     the nav heart ([data-fav-count]) reflects the total. On email
     capture we will later sync this array to a Klaviyo profile (R2.4).
     --------------------------------------------------------- */
  var FAV_KEY = 'arteverk_favs';

  var readFavs = function () {
    try { return JSON.parse(localStorage.getItem(FAV_KEY)) || []; }
    catch (e) { return []; }
  };
  var writeFavs = function (arr) {
    try { localStorage.setItem(FAV_KEY, JSON.stringify(arr)); } catch (e) {}
  };

  var navFav = document.querySelector('[data-fav-nav]');
  var navCount = document.querySelector('[data-fav-count]');
  var favBtns = Array.prototype.slice.call(document.querySelectorAll('[data-fav-id]'));

  var paintCount = function () {
    var n = readFavs().length;
    if (navCount) {
      navCount.textContent = n;
      navCount.hidden = n === 0;
    }
    if (navFav) navFav.classList.toggle('has', n > 0);
  };

  var paintBtn = function (btn) {
    var saved = readFavs().indexOf(btn.dataset.favId) !== -1;
    btn.classList.toggle('saved', saved);
    btn.setAttribute('aria-pressed', String(saved));
  };

  favBtns.forEach(function (btn) {
    // Mark as wired so the load-more re-wire (guarded on _wired) never
    // attaches a SECOND click listener to an original card.
    btn._wired = true;
    paintBtn(btn);
    // Keep the favorite button's saved-label in sync (PDP uses a .fav-label).
    var label = btn.querySelector('.fav-label');
    btn.addEventListener('click', function (e) {
      // The button may live inside the card-wide <a>; stop navigation.
      e.preventDefault();
      e.stopPropagation();
      var favs = readFavs();
      var id = btn.dataset.favId;
      var nowSaved = favs.indexOf(id) === -1;
      favs = nowSaved ? favs.concat([id]) : favs.filter(function (x) { return x !== id; });
      writeFavs(favs);
      paintBtn(btn);
      if (label) label.textContent = nowSaved ? 'Saved' : 'Add to Favorites';
      paintCount();
      if (nowSaved) {
        // GA4: only on ADD (not remove) — favorites feed retargeting.
        avTrack('add_to_wishlist', { items: [{ item_id: id, item_name: btn.dataset.favName || '' }] });
        btn.classList.add('bump');
        setTimeout(function () { btn.classList.remove('bump'); }, 520);
        if (navFav) {
          navFav.classList.add('bump');
          setTimeout(function () { navFav.classList.remove('bump'); }, 520);
        }
      }
    });
  });
  paintCount();

  /* ---------------------------------------------------------
     5 · PRODUCT GALLERY (PDP)
     Thumbnail -> primary crossfade, keyboard arrow nav, and a
     tasteful cursor-follow zoom on the primary image. Guarded:
     does nothing unless a [data-gallery] exists on the page.
     --------------------------------------------------------- */
  var gallery = document.querySelector('[data-gallery]');
  if (gallery) {
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    var stageImgs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-img]'));

    var selectImage = function (i) {
      thumbs.forEach(function (t) {
        var on = +t.dataset.galleryThumb === i;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', String(on));
      });
      stageImgs.forEach(function (im) {
        im.classList.toggle('shown', +im.dataset.galleryImg === i);
      });
    };

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { selectImage(+t.dataset.galleryThumb); });
    });

    var thumbRail = gallery.querySelector('.thumbs');
    if (thumbRail) {
      thumbRail.addEventListener('keydown', function (e) {
        var cur = thumbs.findIndex(function (t) { return t.classList.contains('active'); });
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          var n = (cur + 1) % thumbs.length;
          selectImage(n); thumbs[n].focus();
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var p = (cur - 1 + thumbs.length) % thumbs.length;
          selectImage(p); thumbs[p].focus();
        }
      });
    }

    // Full-screen image zoom is handled by pdp-photoswipe.js (loaded from
    // main-product.liquid): it opens a PhotoSwipe viewer on click and marks the
    // gallery .zoom-ready. The old in-place cursor-zoom was removed.
  }

  /* ---------------------------------------------------------
     6 · PRODUCT VARIANT SELECT (PDP, multi-variant only)
     Updates the hidden cart id + the displayed price when the
     operator's catalog has a genuinely multi-size product.
     --------------------------------------------------------- */
  var variantSelect = document.querySelector('[data-variant-select]');
  if (variantSelect) {
    var variantIdInput = document.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('[data-price]');
    variantSelect.addEventListener('change', function () {
      var opt = variantSelect.options[variantSelect.selectedIndex];
      if (variantIdInput) variantIdInput.value = variantSelect.value;
      if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    });
  }

  /* ---------------------------------------------------------
     6b · STICKY MOBILE BUY BAR (PDP)
     Shows once the main Add-to-Cart button scrolls above the fold;
     its button submits the SAME native product form (one source of
     truth for the cart action).
     --------------------------------------------------------- */
  var mbuy = document.querySelector('[data-mbuy]');
  var productForm = document.querySelector('.product-form');
  if (mbuy && productForm) {
    var mainAdd = productForm.querySelector('.btn-add');
    var mbuyAdd = mbuy.querySelector('[data-mbuy-add]');
    if (mbuyAdd) {
      mbuyAdd.addEventListener('click', function () {
        if (typeof productForm.requestSubmit === 'function') productForm.requestSubmit();
        else productForm.submit();
      });
    }
    if (mainAdd && 'IntersectionObserver' in window) {
      var bo = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var past = !e.isIntersecting && e.boundingClientRect.top < 0;
          // `visibility` (see .mbuy in theme.css) owns accessibility exposure now:
          // it removes the bar from BOTH the accessibility tree and the tab order,
          // and it is CSS-only so it holds if this script never runs. Toggling
          // aria-hidden here as well re-created the exact defect it was meant to
          // prevent: aria-hidden flipped to "true" instantly while visibility
          // stayed visible for the 550ms slide-out, leaving a focusable
          // ADD TO CART inside an aria-hidden subtree for the whole animation.
          mbuy.classList.toggle('show', past);
        });
      }, { threshold: 0 });
      bo.observe(mainAdd);
    }
  }

  /* ---------------------------------------------------------
     6c · RELATED PRODUCTS (Shopify recommendations)
     The PDP renders a same-collection fallback server-side; when the
     section opts into native recommendations, we upgrade in place by
     fetching Shopify's recommendations endpoint and swapping the cards
     (Search & Discovery powered). On any failure the server fallback
     simply stays — so the block is never empty.
     --------------------------------------------------------- */
  var related = document.querySelector('[data-related-recommendations]');
  if (related && 'fetch' in window) {
    var pid = related.getAttribute('data-product-id');
    var sid = related.getAttribute('data-section-id');
    var rlimit = related.getAttribute('data-limit') || 3;
    if (pid && sid) {
      var recUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') +
        'recommendations/products?product_id=' + encodeURIComponent(pid) +
        '&limit=' + encodeURIComponent(rlimit) +
        '&section_id=' + encodeURIComponent(sid);
      fetch(recUrl)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshGrid = doc.querySelector('.related-cards');
          var curGrid = related.querySelector('.related-cards');
          if (freshGrid && curGrid && freshGrid.children.length) {
            curGrid.innerHTML = freshGrid.innerHTML;
            // Reveal + re-wire favorites on the swapped-in cards.
            curGrid.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('in'); });
            curGrid.querySelectorAll('[data-fav-id]').forEach(function (btn) {
              paintBtn(btn);
              btn.addEventListener('click', function (ev) {
                ev.preventDefault(); ev.stopPropagation();
                var favs = readFavs();
                var id = btn.dataset.favId;
                var nowSaved = favs.indexOf(id) === -1;
                favs = nowSaved ? favs.concat([id]) : favs.filter(function (x) { return x !== id; });
                writeFavs(favs); paintBtn(btn); paintCount();
              });
            });
          }
        })
        .catch(function () { /* keep the server fallback */ });
    }
  }

  /* ---------------------------------------------------------
     7 · COLLECTION — facet collapse, mobile drawer, auto-submit
     sort, price-range fill, and Load-more (append next native page).
     Guarded by [data-filters] / [data-loadmore].
     --------------------------------------------------------- */
  var filterForm = document.querySelector('[data-filter-form]');

  // 7a · Collapsible facet groups.
  document.querySelectorAll('[data-facet]').forEach(function (facet) {
    var btn = facet.querySelector('[data-facet-toggle]');
    var body = facet.querySelector('.facet-body');
    if (!btn || !body) return;
    body.style.maxHeight = body.scrollHeight + 'px';
    btn.addEventListener('click', function () {
      var collapsing = !facet.classList.contains('collapsed');
      if (collapsing) {
        body.style.maxHeight = '0px';
        facet.classList.add('collapsed');
        btn.setAttribute('aria-expanded', 'false');
      } else {
        facet.classList.remove('collapsed');
        btn.setAttribute('aria-expanded', 'true');
        body.style.maxHeight = body.scrollHeight + 'px';
      }
    });
  });
  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-facet]:not(.collapsed) .facet-body').forEach(function (b) {
      b.style.maxHeight = b.scrollHeight + 'px';
    });
  }, { passive: true });

  // 7b · Native filtering — submit the form on any facet change so the
  // URL gains ?filter.* params and the server returns filtered results.
  if (filterForm) {
    filterForm.querySelectorAll('[data-filter-input]').forEach(function (input) {
      input.addEventListener('change', function () { filterForm.submit(); });
    });
  }

  // 7c · Auto-submit sort.
  document.querySelectorAll('[data-auto-submit]').forEach(function (sel) {
    sel.addEventListener('change', function () {
      var form = sel.closest('form');
      if (form) form.submit();
    });
  });

  // 7d · Price-range fill visual (does not submit until released, to avoid
  // a request per pixel — the range inputs are part of the filter form, so
  // a change event submits with the chosen min/max).
  var pMin = document.querySelector('[data-price-min]');
  var pMax = document.querySelector('[data-price-max]');
  var pFill = document.querySelector('[data-range-fill]');
  if (pMin && pMax && pFill) {
    var minLab = document.querySelector('[data-price-min-label]');
    var maxLab = document.querySelector('[data-price-max-label]');
    var paintRange = function () {
      var lo = +pMin.value, hi = +pMax.value;
      if (lo > hi) { var t = lo; lo = hi; hi = t; }
      var min = +pMin.min, max = +pMin.max;
      var l = ((lo - min) / (max - min)) * 100;
      var r = ((hi - min) / (max - min)) * 100;
      pFill.style.left = l + '%';
      pFill.style.width = (r - l) + '%';
      if (minLab) minLab.textContent = '$' + Number(lo).toLocaleString('en-US');
      if (maxLab) maxLab.textContent = '$' + Number(hi).toLocaleString('en-US');
    };
    // Two overlapping range inputs share one track — stop the handles from
    // crossing, which would submit a min>max (empty-result) filter on release.
    var clampCross = function (e) {
      if (+pMin.value > +pMax.value) {
        if (e && e.target === pMax) pMax.value = pMin.value;
        else pMin.value = pMax.value;
      }
      paintRange();
    };
    pMin.addEventListener('input', clampCross);
    pMax.addEventListener('input', clampCross);
    paintRange();
    // NOTE: the price inputs now carry data-filter-input, so 7b submits the
    // filter form on `change` (fires on handle release — one request per drag).
  }

  // 7e · Mobile filter drawer.
  var filtersEl = document.querySelector('[data-filters]');
  var scrim = document.querySelector('[data-filter-scrim]');
  var openTrigger = document.querySelector('[data-filter-open]');
  var closeTrigger = document.querySelector('[data-filter-close]');
  if (filtersEl && scrim) {
    var openDrawer = function () {
      filtersEl.classList.add('open'); scrim.classList.add('open'); document.body.style.overflow = 'hidden';
      // Mark as a modal dialog for assistive tech ONLY while open (it is a plain
      // sidebar on desktop, so role/aria-modal are added per-open, removed on close).
      filtersEl.setAttribute('role', 'dialog');
      filtersEl.setAttribute('aria-modal', 'true');
      // Move focus into the drawer (its close button) for keyboard users.
      if (closeTrigger) closeTrigger.focus();
    };
    var closeDrawer = function () {
      filtersEl.classList.remove('open'); scrim.classList.remove('open'); document.body.style.overflow = '';
      filtersEl.removeAttribute('role');
      filtersEl.removeAttribute('aria-modal');
      // Restore focus to the control that opened the drawer.
      if (openTrigger) openTrigger.focus();
    };
    if (openTrigger) openTrigger.addEventListener('click', openDrawer);
    if (closeTrigger) closeTrigger.addEventListener('click', closeDrawer);
    scrim.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && filtersEl.classList.contains('open')) closeDrawer();
    });
    // Trap Tab within the drawer while open (mirrors the mobile-menu trap).
    filtersEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !filtersEl.classList.contains('open')) return;
      var f = Array.prototype.slice.call(filtersEl.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  // Header · mega-menu keyboard support: aria-expanded state + Escape-to-close.
  // The panels open on CSS :hover/:focus-within; this adds the announced state
  // and an Escape that collapses the panel and returns focus to its trigger.
  document.querySelectorAll('#site-header .nav-item.has-drop').forEach(function (item) {
    var trigger = item.querySelector('.nav-top');
    if (!trigger) return;
    var set = function (v) { trigger.setAttribute('aria-expanded', String(v)); };
    set(false);
    item.addEventListener('focusin', function () { item.classList.remove('kb-collapsed'); set(true); });
    item.addEventListener('mouseenter', function () { item.classList.remove('kb-collapsed'); set(true); });
    item.addEventListener('mouseleave', function () { set(false); });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) { set(false); item.classList.remove('kb-collapsed'); }
    });
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { item.classList.add('kb-collapsed'); set(false); trigger.focus(); }
    });
  });

  // 7f · Load more — fetch the next native page and append its cards.
  var loadmore = document.querySelector('[data-loadmore]');
  if (loadmore) {
    var nextLink = loadmore.querySelector('[data-loadmore-next]');
    var grid = document.querySelector('[data-grid]');
    var progress = loadmore.querySelector('.progress');
    var totalItems = null;

    var doLoad = function (e) {
      if (e) e.preventDefault();
      if (!nextLink || !grid) return;
      var url = nextLink.getAttribute('href');
      var ring = nextLink.querySelector('.ring');
      if (ring) ring.textContent = '…';

      fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newCards = doc.querySelectorAll('[data-grid] > .card');
          newCards.forEach(function (c) {
            grid.appendChild(c);
            // Re-run reveal for appended cards.
            c.classList.add('in');
          });

          // Re-wire favorite buttons on the appended cards.
          grid.querySelectorAll('[data-fav-id]').forEach(function (btn) {
            if (btn._wired) return;
            btn._wired = true;
            paintBtn(btn);
            btn.addEventListener('click', function (ev) {
              ev.preventDefault(); ev.stopPropagation();
              var favs = readFavs();
              var id = btn.dataset.favId;
              var nowSaved = favs.indexOf(id) === -1;
              favs = nowSaved ? favs.concat([id]) : favs.filter(function (x) { return x !== id; });
              writeFavs(favs);
              paintBtn(btn);
              paintCount();
              if (nowSaved) {
                btn.classList.add('bump');
                setTimeout(function () { btn.classList.remove('bump'); }, 520);
              }
            });
          });

          // Advance the next link, or remove the button when exhausted.
          var newNext = doc.querySelector('[data-loadmore-next]');
          if (newNext) {
            nextLink.setAttribute('href', newNext.getAttribute('href'));
            if (ring) ring.textContent = '↓';
          } else {
            nextLink.remove();
          }

          // Update the progress line + bar from the freshly fetched page.
          var newProgress = doc.querySelector('[data-loadmore] .progress');
          if (newProgress && progress) progress.textContent = newProgress.textContent.trim();
          var newBar = doc.querySelector('[data-loadmore] .bar i');
          var bar = loadmore.querySelector('.bar i');
          if (newBar && bar) bar.style.width = newBar.style.width;
        })
        .catch(function () {
          // On failure, fall back to a normal navigation.
          if (nextLink) window.location.href = nextLink.getAttribute('href');
        });
    };

    if (nextLink) nextLink.addEventListener('click', doLoad);
  }

  /* ---------------------------------------------------------
     8 · CART QUANTITY STEPPERS
     The cart form is fully keyed (name="updates[<key>]"). With JS,
     the +/- buttons nudge the adjacent [data-qty-input] and submit
     the cart form so the change applies. Guarded: does nothing
     unless the cart form and at least one stepper exist.
     --------------------------------------------------------- */
  var cartForm = document.getElementById('cart');
  if (cartForm && cartForm.querySelector('[data-qty-input]')) {
    var stepQty = function (input, delta) {
      if (!input) return;
      var min = input.hasAttribute('min') ? parseInt(input.getAttribute('min'), 10) : 1;
      if (isNaN(min)) min = 1;
      var max = input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : null;
      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = min;
      var next = current + delta;
      if (next < min) next = min;
      if (max !== null && !isNaN(max) && next > max) next = max;
      if (next === current) return;
      input.value = next;
      if (typeof cartForm.requestSubmit === 'function') cartForm.requestSubmit();
      else cartForm.submit();
    };

    cartForm.querySelectorAll('[data-qty-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('[data-qty-input]');
      var minus = stepper.querySelector('[data-qty-minus]');
      var plus = stepper.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', function () { stepQty(input, -1); });
      if (plus) plus.addEventListener('click', function () { stepQty(input, 1); });
    });
  }
})();
